<template>
<div data-component="FilterDetailsMatching.vue">
	<!-- Dodatkowe ustawienia -->
	<div
		:class="{'inactive-panel': (selectedFilter.type === 'quick' && !isAdminModule) || (selectedFilter.type === 'automatic' && !selectedFilter.isActive) }"
		@change="onChange"
	>
		<!-- MPO -->
		<template v-if="(isAdminModule) || selectedFilter.type === 'free'">
			<div v-for="mpo in selectedFilter.mpo" :key="mpo.id" class="mb-6">
				<div class="mb-3 font-extrabold" v-html="mpo.formLabel"/>
				<label
					v-for="answerDtoObject in mpo.optionsList"
					:key="answerDtoObject.id"
					class="flex items-center my-3"
				>
					<div class="mx-3 input-control">
						<input
							v-model="answerDtoObject.checked"
							:disabled="((mpo.optionsList.filter((option) => option.checked).length === 1) && answerDtoObject.checked)"
							type="checkbox"
						>
						<div/>
					</div>
					<div v-html="answerDtoObject.formLabel"/>
				</label>
			</div>
		</template>

		<!-- Wielkość, wiek  -->
		<template v-for="filterType in sizeFilterFields" :key="filterType">
			<div v-if="(selectedFilter.filterTypes[filterType] && selectedFilter.filterTypes[filterType].unit)" class="mb-6">
				<div class="font-extrabold" v-html="filterType === 'size' ? 'Wielkość' : 'Wiek'"/>
				<div class="flex items-center">
					Od
					<div class="flex-none w-20 mx-1 mt-3 form-input">
						<input
							v-if="selectedFilter.filterTypes[filterType]"
							v-model="selectedFilter.filterTypes[filterType].minValue"
							class="p-0 text-center"
							type="number"
							:min="selectedFilter.filterTypes[filterType].minLimit"
							:max="selectedFilter.filterTypes[filterType].maxValue
								|| selectedFilter.filterTypes[filterType].maxLimit"
							@blur="filterTypeRange($event, 'from')"
							@keydown="filterTypeRangeKeydown"
						>
					</div>
					do
					<div class="flex-none w-20 mx-1 mt-3 form-input">
						<input
							v-model="selectedFilter.filterTypes[filterType].maxValue"
							class="p-0 text-center"
							type="number"
							:min="selectedFilter.filterTypes[filterType].minValue
								|| selectedFilter.filterTypes[filterType].minLimit"
							:max="selectedFilter.filterTypes[filterType].maxLimit"
							@blur="filterTypeRange($event, 'to')"
							@keydown="filterTypeRangeKeydown"
						>
					</div>
					<span v-html="units[(selectedFilter.filterTypes[filterType].unit || 'YEARS')]"/>
				</div>
			</div>
		</template>

		<!-- Cechy klientów -->
		<div class="mb-6">
			<div class="mb-3 font-extrabold" v-html="'Cechy klientów'"/>
			<template v-for="filterType in rfpTypeFilter">
				<label
					v-if="!(selectedFilter.filterTypes.hideNotConfirmedBySms && filterType === 'notConfirmedBySms')"
					:key="filterType"
					class="flex items-center my-3"
				>
					<div class="mx-3 input-control">
						<input
							v-model="selectedFilter.filterTypes[filterType]"
							type="checkbox"
						>
						<div/>
					</div>
					<div v-text="typeLabel[filterType]"/>
				</label>
			</template>
		</div>
	</div>
</div>
</template>
<script lang="ts">
import { mapWritableState, mapState } from 'pinia';
import { useServicesStore } from '../../../../stores/services';
import { useAppStore } from '../../../../stores/app';
import Filter from '../../../../types/interfaces/services/Filter';

type FilterSizeType = 'size'|'age';
type FilterRfpType = 'discounted'|'individuals'|'notConfirmedBySms';

/**
 * Ekran dostosowywania filtra (MPO, rodzaje zleceń, wielkość, etc.)
 */
export default {
	name: 'FilterDetailsMatching',
	props: {

		/**
		 * Aktualnie wybrany filtr
		 */
		filter: {
			type: Object as () => Filter,
			required: true,
		},
	},
	emits: ['recalculate-potential-matches'],
	data() {
		return {
			/**
			 * Aktualnie wybrany filtr
			 */
			selectedFilter: this.filter,

			/**
			 * Etykiety dla różnych rodzajów zleceń
			 */
			typeLabel: {
				discounted: 'Klienci z obniżoną ceną kontaktu',
				individuals: 'Osoby prywatne',
				notConfirmedBySms: 'Klienci z niepotwierdzonym numerem telefonu',
			},

			/**
			 * Jednostki do pokazania w filtrze "Wielkość"
			 */
			units: {
				M2: 'm²',
				M3: 'm³',
				MB: 'mb',
				KG: 'kg',
				PT: 'punktów',
				PUNKT: 'punktów',
				OSOBA: 'osób',
				OSOBY: 'osób',
				'SZT.': 'sztuk',
				ZŁ: 'złotych',
				T: 'ton',
				YEARS: 'lat',
			},

			/**
			 * Wielkość
			 */
			sizeFilterFields: ['size', 'age'] as FilterSizeType[],

			rfpTypeFilter: ['discounted', 'individuals', 'notConfirmedBySms'] as FilterRfpType[],
		};
	},
	computed: {
		...mapState(useAppStore, [
			'isAdminModule',
		]),
		...mapWritableState(useServicesStore, [
			'isFilterDirty',
		]),
	},
	methods: {
		onChange() {
			this.isFilterDirty = true;
			this.$emit('recalculate-potential-matches');
		},
		/**
		 * W przypadku filtrów "Wielkość" oraz "Wiek", sprawdza czy wprowadzona maksymalna
		 * wartość nie jest mniejsza od wartości minimalnej.
		 */
		filterTypeRange(e: FocusEvent, inputType: string) {
			const target = e.target as HTMLInputElement;

			if (!target) {
				return;
			}

			const min = Number(target.getAttribute('min'));
			const max = Number(target.getAttribute('max'));

			let { valueAsNumber } = target;

			if (valueAsNumber > max) {
				valueAsNumber = max;
			}

			if (valueAsNumber < min) {
				valueAsNumber = min;
			}

			target.valueAsNumber = Math.floor(valueAsNumber);

			if (inputType === 'from') {
				this.selectedFilter.filterTypes['size'].minValue = valueAsNumber || null;
			} else {
				this.selectedFilter.filterTypes['size'].maxValue = valueAsNumber || null;
			}
		},

		/**
		 * Blokuje możliwość wpisania znaków innych, niż cyfry.
		 */
		filterTypeRangeKeydown(e: KeyboardEvent) {
			if (((e.keyCode >= 40 && e.keyCode <= 126) && !(e.keyCode >= 48 && e.keyCode <= 57)) || e.shiftKey || e.key === '-') {
				e.preventDefault();
			}
		},
	},
};
</script>
